import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Video, Sparkles, Mic, Scissors, Wand2, Send, CheckCircle2, Users, Crown, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const features = [
  { icon: Mic, label: "Auto-transcribe streams & uploads" },
  { icon: Scissors, label: "AI finds viral moments → clips" },
  { icon: Wand2, label: "Auto-brand: logo, fonts, CTA, subtitles" },
  { icon: Send, label: "Auto-post to TikTok / Reels / Shorts / X" },
  { icon: Sparkles, label: "AI titles, thumbnails, hooks" },
  { icon: Users, label: "Team + agency white-label" },
];

export default function CreatorPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [count, setCount] = useState(0);
  const [email, setEmail] = useState(user?.email || "");
  const [name, setName] = useState(user?.name || "");
  const [planInterest, setPlanInterest] = useState("creator_pro");
  const [useCase, setUseCase] = useState("creator");
  const [note, setNote] = useState("");
  const [joined, setJoined] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get("/creator/plans").then(({ data }) => setPlans(data)).catch(() => {});
    api.get("/creator/waitlist/count").then(({ data }) => setCount(data.count)).catch(() => {});
  }, []);

  // Return from Stripe pre-order
  useEffect(() => {
    const sid = params.get("preorder_session_id");
    const cancelled = params.get("preorder_cancelled");
    if (cancelled) { toast.info("Pre-order cancelled"); setParams({}, { replace: true }); return; }
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/creator/preorder/status/${sid}`);
        if (data.payment_status === "paid" && data.credited) {
          setParams({}, { replace: true });
          setJoined(true);
          toast.success("Pre-order locked in", {
            description: `You're founding member #${count + 1}. We'll email when Creator OS launches.`,
            duration: 12000,
          });
          return;
        }
      } catch (err) { /* keep polling */ }
      attempts += 1;
      if (attempts < 10) setTimeout(poll, 2000);
      else setParams({}, { replace: true });
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const joinWaitlist = async () => {
    if (!email) { toast.error("Enter your email"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/creator/waitlist", {
        email, name, plan_interest: planInterest, use_case: useCase, note,
      });
      if (data.waitlist_total) setCount(data.waitlist_total);
      setJoined(true);
      toast.success(data.already_joined ? "Already on the list" : "You're on the waitlist 🎉");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not join");
    } finally { setSubmitting(false); }
  };

  const preorder = async (planId) => {
    if (!user) { toast.error("Please log in to pre-order"); return; }
    // Route Creator OS pre-orders through the same manual confirm flow as Pricing
    // (PayPal / Revolut / WhatsApp) instead of Stripe.
    try {
      await api.post("/orders", {
        product_id: planId === "agency" ? "business_plan" : planId === "creator_elite" ? "premium_month" : "premium_month",
        method: "paypal",
        payment_reference: "creator_os_preorder",
        note: `Creator OS pre-order: ${planId}`,
      });
      toast.success("Pre-order recorded", {
        description: "Pay £" + (plans.find(p => p.id === planId)?.price || "") + " via PayPal.me/OlamideEmmanuelOjo, then submit your reference on /pricing.",
        duration: 12000,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record pre-order");
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-12 md:px-10">
      {/* Hero */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-xs uppercase tracking-[0.2em] text-accent">
          <Sparkles className="h-3 w-3" /> Coming soon
        </div>
        <h1 className="mt-4 font-display text-4xl font-medium tracking-tighter sm:text-5xl lg:text-6xl">
          Zuntria <span className="text-accent">Creator OS</span>
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground sm:text-lg">
          One long-form video → dozens of viral clips, auto-branded, auto-posted to every platform.
          You hit upload. The AI does the rest.
        </p>
        <div className="mt-4 text-sm text-muted-foreground" data-testid="waitlist-counter">
          🔥 <strong className="text-foreground">{count}</strong> creators on the waitlist
        </div>
      </div>

      {/* Feature grid */}
      <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {features.map(({ icon: Icon, label }, i) => (
          <div key={i} className="flex items-start gap-3 rounded-2xl border border-border bg-card p-5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-4 w-4" /></span>
            <span className="text-sm">{label}</span>
          </div>
        ))}
      </div>

      {/* Pricing */}
      <div className="mt-16">
        <p className="text-center text-xs uppercase tracking-[0.2em] text-muted-foreground">Founding member pricing — lock in for life</p>
        <h2 className="mt-2 text-center font-display text-3xl">Pre-order &amp; never pay full price.</h2>
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
          {plans.map((p, i) => (
            <div key={p.id} className={`flex flex-col rounded-2xl border bg-card p-6 ${i === 1 ? "border-accent glow-cobalt" : "border-border"}`} data-testid={`plan-${p.id}`}>
              {i === 1 && <div className="mb-3 inline-flex w-fit items-center rounded-full bg-accent/15 px-2 py-0.5 text-xs font-medium text-accent"><Crown className="mr-1 h-3 w-3" /> Most chosen</div>}
              <h3 className="font-display text-xl">{p.name}</h3>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-display text-3xl">£{p.price}</span>
                <span className="text-sm text-muted-foreground">/{p.billing}</span>
              </div>
              <ul className="mt-5 space-y-2 text-sm">
                {p.perks.map((perk) => (
                  <li key={perk} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /><span>{perk}</span></li>
                ))}
              </ul>
              <Button
                onClick={() => preorder(p.id)}
                disabled={creating || !user}
                className={`mt-auto h-12 w-full ${i === 1 ? "bg-accent text-accent-foreground hover:bg-accent/90" : ""}`}
                data-testid={`preorder-${p.id}`}
              >
                Pre-order £{p.price}/mo <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
              {!user && <p className="mt-2 text-center text-[10px] text-muted-foreground">Log in to pre-order</p>}
            </div>
          ))}
        </div>
      </div>

      {/* Free waitlist */}
      <div className="mt-16 rounded-3xl border border-border bg-gradient-to-br from-primary/5 via-card to-accent/5 p-8 md:p-12" data-testid="waitlist-card">
        {joined ? (
          <div className="text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
            <h3 className="mt-4 font-display text-2xl">You're in 🎉</h3>
            <p className="mt-2 text-sm text-muted-foreground">We'll email <strong className="text-foreground">{email}</strong> the moment Creator OS opens. Want to lock in founding-member pricing? Pre-order above.</p>
          </div>
        ) : (
          <>
            <div className="text-center">
              <Video className="mx-auto h-8 w-8 text-primary" />
              <h3 className="mt-3 font-display text-2xl">Or just join the free waitlist</h3>
              <p className="mt-2 text-sm text-muted-foreground">No payment. We'll email you at launch with early-bird pricing.</p>
            </div>
            <div className="mx-auto mt-8 grid max-w-2xl grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-1.5"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="h-11" data-testid="wl-name" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="wl-email" /></div>
              <div className="space-y-1.5">
                <Label>I'm a…</Label>
                <select value={useCase} onChange={(e) => setUseCase(e.target.value)} className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="wl-use-case">
                  <option value="creator">Creator / YouTuber</option>
                  <option value="coach">Coach</option>
                  <option value="podcaster">Podcaster</option>
                  <option value="business">Business owner</option>
                  <option value="agency">Agency</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Plan I'm eyeing</Label>
                <select value={planInterest} onChange={(e) => setPlanInterest(e.target.value)} className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="wl-plan">
                  <option value="">Not sure yet</option>
                  {plans.map((p) => <option key={p.id} value={p.id}>{p.name} (£{p.price}/mo)</option>)}
                </select>
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label>What would you use it for? (optional)</Label>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="min-h-[70px]" placeholder="e.g. clipping my weekly podcast into TikTok shorts" data-testid="wl-note" />
              </div>
              <Button onClick={joinWaitlist} disabled={submitting} className="h-12 md:col-span-2" data-testid="wl-submit">
                {submitting ? "Joining…" : "Join the waitlist — free"}
              </Button>
            </div>
          </>
        )}
      </div>

      <p className="mt-10 text-center text-xs text-muted-foreground">
        Built by Zuntria. Pre-orders are processed by Stripe. Refundable at launch if you change your mind within 14 days.
      </p>
    </div>
  );
}
